import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/freeGuide.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FreeGuide = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Free, 7 AI Prompts That Build Discipline | Disciplined Me";
  }, []);

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = "Please enter your first name.";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errs.email = "Please enter your email address.";
    } else if (!emailRegex.test(email.trim())) {
      errs.email = "Please enter a valid email address.";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { name: name.trim(), email: email.trim() });
      navigate("/free-guide/thank-you");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setErrors({ form: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="site-page page-free-guide">
      <nav><div className="wrap"><div className="logo">disciplined<span>.me</span></div><Link to="/" className="nav-back">← Back home</Link></div></nav>

      <section className="hero">
        <div className="wrap">
          <span className="free-badge">100% free, delivered instantly</span>
          <h1>You don't need more motivation.<br />You need <span className="hl">a system.</span></h1>
          <p className="hero-sub">7 AI prompts that turn any AI tool into a personal discipline coach, free, and ready to use in the next 5 minutes.</p>

          <form className="optin" onSubmit={handleSubmit} noValidate>
            <div className="optin-cover">
              <img src="/images/free-guide-cover.png" alt="7 AI Prompts That Build Discipline — cover" />
            </div>
            <input
              className={`field ${errors.name ? "has-error" : ""}`}
              type="text"
              placeholder="First name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {errors.name && <div className="field-error">{errors.name}</div>}
            <input
              className={`field ${errors.email ? "has-error" : ""}`}
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <div className="field-error">{errors.email}</div>}
            {errors.form && <div className="field-error" style={{ textAlign: "center", marginTop: "4px" }}>{errors.form}</div>}
            <button className="optin-btn" type="submit" disabled={submitting}>
              {submitting ? "Sending…" : "Send me the free prompts →"}
            </button>
            <div className="optin-micro">Free forever. Delivered straight to your inbox.</div>
          </form>
        </div>
      </section>

      <section className="inside">
        <div className="wrap">
          <div className="inside-head">
            <div className="eyebrow">What's inside</div>
            <h2>Seven prompts. One for every moment that matters.</h2>
          </div>
          <div className="prompt-grid">
            <div className="prompt-card"><div className="p-num">01</div><div className="p-body"><h4>Morning Intention</h4><p>Start every day with clarity, before the noise gets in.</p></div></div>
            <div className="prompt-card"><div className="p-num">02</div><div className="p-body"><h4>Evening Reflection</h4><p>Extract the lesson from every day in under 8 minutes.</p></div></div>
            <div className="prompt-card"><div className="p-num">03</div><div className="p-body"><h4>Focus Reset</h4><p>Break any distraction spiral in under 5 minutes.</p></div></div>
            <div className="prompt-card"><div className="p-num">04</div><div className="p-body"><h4>Goal Breakdown</h4><p>Turn any overwhelming goal into this week's one action.</p></div></div>
            <div className="prompt-card"><div className="p-num">05</div><div className="p-body"><h4>Accountability Check-in</h4><p>Hold yourself to what you said you'd do, no one else watching.</p></div></div>
            <div className="prompt-card"><div className="p-num">06</div><div className="p-body"><h4>Setback Debrief</h4><p>Recover from any missed day or failed streak in under 10 minutes.</p></div></div>
          </div>
        </div>
      </section>

      <section className="trust">
        <div className="wrap">
          <h2>Not a generic prompt list</h2>
          <p className="lora">"Every prompt here is built around one idea: discipline isn't a personality trait, it's a system, and AI is the missing piece that makes that system actually stick."</p>
        </div>
      </section>

      <section className="cta2">
        <div className="wrap">
          <h2>Five minutes from now, you could already be using this.</h2>
          <p>No app to download. No subscription. Just seven prompts and the AI tool you already have.</p>
          <a href="#top" className="btn-primary" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Send me the free prompts →</a>
        </div>
      </section>

      <footer>© Disciplined Me, Building the identity of someone who keeps going.</footer>
    </div>
  );
};

export default FreeGuide;
